import { Formik, Form } from 'formik';
import * as Yup from 'yup';
import { useEffect, useState, useCallback, useRef } from 'react';
import { requireArenaSlug } from '@/utils/arena';
import { arenaService } from '@/services/arenaService';
import { Icon } from '@iconify/react';

// Types
export interface AkunValues {
  email: string;
  currentPassword: string;
  newPassword: string;
  confirmNewPassword: string;
  accountName: string;
  bank: string;
  accountNumber: string;
}

// Initial Values
const akunInitial: AkunValues = {
  email: '',
  currentPassword: '',
  newPassword: '',
  confirmNewPassword: '',
  accountName: '',
  bank: '',
  accountNumber: '',
};

// Validation Schema
const akunSchema = Yup.object({
  email: Yup.string().email('Format email tidak valid').required('Email wajib diisi'),
  accountName: Yup.string().required('Nama pemilik rekening wajib diisi'),
  bank: Yup.string().required('Pilihan bank wajib diisi'),
  accountNumber: Yup.string().required('Nomor rekening wajib diisi'),
});

interface AkunTabProps {
  onSubmit: (values: AkunValues, tabId: string) => Promise<void>;
}

const AkunTab = ({ onSubmit }: AkunTabProps) => {
  const [initialValues, setInitialValues] = useState<AkunValues>(akunInitial);
  const [showPassword, setShowPassword] = useState(false);
  const mountedRef = useRef(true);

  const bankOptions = [
    { value: '', label: 'Pilih Bank' },
    { value: 'ID_MANDIRI', label: 'Mandiri' },
    { value: 'ID_BCA', label: 'BCA' },
    { value: 'ID_BRI', label: 'BRI' },
    { value: 'ID_BNI', label: 'BNI' },
    { value: 'ID_CIMB', label: 'CIMB Niaga' },
    { value: 'ID_DANAMON', label: 'Danamon' },
    { value: 'ID_PERMATA', label: 'Permata' },
    { value: 'ID_MEGA', label: 'Mega' },
    { value: 'ID_BTN', label: 'BTN' },
    { value: 'ID_BII', label: 'BII (Maybank)' },
    { value: 'ID_JAGO', label: 'Jago' },
    { value: 'ID_SEABANK', label: 'SeaBank Indonesia' },
  ];

  const fetchSportoArena = useCallback(async () => {
    try {
      const slug = requireArenaSlug();
      const sportoArena = await arenaService.getArenaBySlug(slug);
      if (!mountedRef.current || !sportoArena) return;

      const values: AkunValues = {
        email: sportoArena.owner?.email || sportoArena.email || '',
        currentPassword: '',
        newPassword: '',
        confirmNewPassword: '',
        accountName: sportoArena.payment_account?.name || '',
        bank: sportoArena.payment_account?.bank || '',
        accountNumber: sportoArena.payment_account?.number || '',
      };

      setInitialValues(values);
    } catch (_err) {
      // ignore
    }
  }, []);

  useEffect(() => {
    mountedRef.current = true;
    fetchSportoArena();
    return () => {
      mountedRef.current = false;
    };
  }, [fetchSportoArena]);

  const handleSubmit = async (values: AkunValues) => {
    try {
      const slug = requireArenaSlug();

      const payload = {
        email: values.email,
        current_password: values.currentPassword || undefined,
        confirm_new_password: values.confirmNewPassword || undefined,
        new_password: values.newPassword || undefined,
        name: values.accountName,
        bank: values.bank,
        account_number: values.accountNumber,
      };

      const res = await arenaService.updateSportoArena(slug, payload);
      if (res.success) {
        await fetchSportoArena();
        await onSubmit(values, 'akun');
      } else {
        throw new Error(res.message || 'Gagal menyimpan pengaturan akun');
      }
    } catch (err: any) {
      console.error('Update sporto arena failed', err);
      throw err;
    }
  };

  return (
    <Formik
      initialValues={initialValues}
      enableReinitialize
      validationSchema={akunSchema}
      onSubmit={async (values, helpers) => {
        try {
          await handleSubmit(values);
        } catch (err: any) {
          if (err?.message) {
            helpers.setStatus({ message: err.message });
          }
        } finally {
          helpers.setSubmitting(false);
        }
      }}
    >
      {({ values, errors, touched, handleChange, isSubmitting, status }) => (
        <Form id="active-profile-form" className="space-y-6 max-w-8xl">
          {status?.message && (
            <div className="p-4 rounded-xl bg-red-50 border border-red-200 text-red-600 text-xs font-medium">
              {status.message}
            </div>
          )}

          {/* Section 1: Informasi Akun & Keamanan */}
          <div className="bg-white rounded-2xl border border-gray-200/80 p-6 shadow-2xs space-y-5">
            <div className="text-[11px] font-semibold text-gray-400 tracking-wider uppercase">
              INFORMASI AKUN & KEAMANAN
            </div>

            <div>
              <label className="text-xs font-semibold text-[#0E1726] mb-1.5 block">
                Email Akun Pengelola
              </label>
              <input
                type="email"
                name="email"
                value={values.email}
                onChange={handleChange}
                placeholder="nama@arena.com"
                className="w-full h-11 px-4 bg-white border border-gray-200 rounded-xl text-xs text-[#0E1726] placeholder:text-gray-400 focus:outline-none focus:border-[#FC480D] focus:ring-1 focus:ring-[#FC480D] transition-colors"
              />
              {errors.email && touched.email && (
                <p className="text-red-500 text-[11px] mt-1">{errors.email}</p>
              )}
            </div>

            <div className="pt-2 border-t border-gray-100">
              <div className="flex items-center justify-between mb-3">
                <span className="text-xs font-semibold text-[#0E1726]">Ganti Password</span>
                <button
                  type="button"
                  onClick={() => setShowPassword((prev) => !prev)}
                  className="text-xs text-[#FC480D] hover:underline font-semibold cursor-pointer"
                >
                  {showPassword ? 'Sembunyikan' : 'Perbarui Password'}
                </button>
              </div>

              {showPassword && (
                <div className="space-y-4 pt-1 animate-in fade-in duration-200">
                  <div>
                    <label className="text-xs font-semibold text-[#0E1726] mb-1.5 block">
                      Password Saat Ini
                    </label>
                    <input
                      type="password"
                      name="currentPassword"
                      value={values.currentPassword}
                      onChange={handleChange}
                      placeholder="Masukkan password saat ini"
                      className="w-full h-11 px-4 bg-white border border-gray-200 rounded-xl text-xs text-[#0E1726] placeholder:text-gray-400 focus:outline-none focus:border-[#FC480D] focus:ring-1 focus:ring-[#FC480D] transition-colors"
                    />
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="text-xs font-semibold text-[#0E1726] mb-1.5 block">
                        Password Baru
                      </label>
                      <input
                        type="password"
                        name="newPassword"
                        value={values.newPassword}
                        onChange={handleChange}
                        placeholder="Minimal 8 karakter"
                        className="w-full h-11 px-4 bg-white border border-gray-200 rounded-xl text-xs text-[#0E1726] placeholder:text-gray-400 focus:outline-none focus:border-[#FC480D] focus:ring-1 focus:ring-[#FC480D] transition-colors"
                      />
                    </div>
                    <div>
                      <label className="text-xs font-semibold text-[#0E1726] mb-1.5 block">
                        Konfirmasi Password Baru
                      </label>
                      <input
                        type="password"
                        name="confirmNewPassword"
                        value={values.confirmNewPassword}
                        onChange={handleChange}
                        placeholder="Ulangi password baru"
                        className="w-full h-11 px-4 bg-white border border-gray-200 rounded-xl text-xs text-[#0E1726] placeholder:text-gray-400 focus:outline-none focus:border-[#FC480D] focus:ring-1 focus:ring-[#FC480D] transition-colors"
                      />
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Section 2: Rekening Bank Pencairan */}
          <div className="bg-white rounded-2xl border border-gray-200/80 p-6 shadow-2xs space-y-5">
            <div className="text-[11px] font-semibold text-gray-400 tracking-wider uppercase">
              REKENING BANK PENCAIRAN
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              <div>
                <label className="text-xs font-semibold text-[#0E1726] mb-1.5 block">
                  Nama Pemilik Rekening
                </label>
                <input
                  type="text"
                  name="accountName"
                  value={values.accountName}
                  onChange={handleChange}
                  placeholder="Sesuai buku tabungan"
                  className="w-full h-11 px-4 bg-white border border-gray-200 rounded-xl text-xs text-[#0E1726] placeholder:text-gray-400 focus:outline-none focus:border-[#FC480D] focus:ring-1 focus:ring-[#FC480D] transition-colors"
                />
                {errors.accountName && touched.accountName && (
                  <p className="text-red-500 text-[11px] mt-1">{errors.accountName}</p>
                )}
              </div>

              <div>
                <label className="text-xs font-semibold text-[#0E1726] mb-1.5 block">
                  Pilihan Bank
                </label>
                <div className="relative">
                  <select
                    name="bank"
                    value={values.bank}
                    onChange={handleChange}
                    className="w-full h-11 px-4 pr-10 bg-white border border-gray-200 rounded-xl text-xs text-[#0E1726] focus:outline-none focus:border-[#FC480D] focus:ring-1 focus:ring-[#FC480D] transition-colors appearance-none cursor-pointer"
                  >
                    {bankOptions.map((opt) => (
                      <option key={opt.value} value={opt.value}>
                        {opt.label}
                      </option>
                    ))}
                  </select>
                  <Icon
                    icon="ph:caret-down-bold"
                    className="w-3.5 h-3.5 text-gray-400 absolute right-3.5 top-4 pointer-events-none"
                  />
                </div>
                {errors.bank && touched.bank && (
                  <p className="text-red-500 text-[11px] mt-1">{errors.bank}</p>
                )}
              </div>
            </div>

            <div>
              <label className="text-xs font-semibold text-[#0E1726] mb-1.5 block">
                Nomor Rekening
              </label>
              <input
                type="text"
                name="accountNumber"
                value={values.accountNumber}
                onChange={handleChange}
                placeholder="1234567890"
                className="w-full h-11 px-4 bg-white border border-gray-200 rounded-xl text-xs text-[#0E1726] placeholder:text-gray-400 focus:outline-none focus:border-[#FC480D] focus:ring-1 focus:ring-[#FC480D] transition-colors"
              />
              {errors.accountNumber && touched.accountNumber && (
                <p className="text-red-500 text-[11px] mt-1">{errors.accountNumber}</p>
              )}
            </div>
          </div>

          {/* Bottom Save Action */}
          <div className="flex justify-end pt-2">
            <button
              type="submit"
              disabled={isSubmitting}
              className="bg-[#FC480D] hover:bg-[#E8490F] text-white text-xs font-semibold px-6 py-2.5 rounded-full transition-all cursor-pointer shadow-2xs active:scale-[0.98] disabled:opacity-50"
            >
              {isSubmitting ? 'Menyimpan...' : 'Simpan Perubahan'}
            </button>
          </div>
        </Form>
      )}
    </Formik>
  );
};

export default AkunTab;
